import { useRequiredContext } from "@redotech/react-util/context";
import { PaymentProviderType } from "@redotech/redo-model/billing/billing-engine-types";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
} from "@redotech/redo-model/billing/service-invoice";
import {
  centsToDollars,
  dollarsToCents,
  UsdCents,
  UsdDollars,
} from "@redotech/redo-model/currency";
import {
  BaseSubscriptionBillingSettings,
  teamGetPaymentMethod,
} from "@redotech/redo-model/team";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { DateInput } from "@redotech/redo-web/date-picker";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import {
  areDatesEqual,
  getDateStartOfDayInTimezone,
} from "@redotech/util/date";
import { memo, useEffect, useMemo } from "react";
import { TeamContext } from "../../../../team";

export const TARGET_BILLING_DAY_OPTIONS = Array.from(
  { length: 28 },
  (_, i) => ({
    id: `day-${i + 1}`,
    type: "text" as const,
    text: `${i + 1}`,
    value: i + 1,
  }),
);

export const subscriptionBillingForm = groupInput(
  {
    enabled: input<boolean>(),
    paymentProvider: input<PaymentProviderType>(),
    renewOnPeriodEnd: input<boolean>(),
    startDate: input<Date | null>({ equal: areDatesEqual }),
    period: input<BillingPeriod>(),
    periodFeeInDollars: input<number>(),
    targetBillingDay: input<number | null>(),
    isStartDateInFuture: input<boolean>(),
  },
  {
    validator: (value) => {
      if (value.enabled && !value.startDate) {
        return ["Start date is required when billing is enabled"];
      }
      if (!value.isStartDateInFuture) {
        return ["Start date must not have already passed"];
      }
      return [];
    },
  },
);

export type SubscriptionBillingForm = InputProvider.Form<
  typeof subscriptionBillingForm
>;

export type SubscriptionBillingValue = InputProvider.Value<
  typeof subscriptionBillingForm
>;

export const subscriptionBillingDefault: SubscriptionBillingValue = {
  enabled: false,
  renewOnPeriodEnd: false,
  startDate: null,
  period: BillingPeriod.MONTHLY,
  periodFeeInDollars: 0,
  targetBillingDay: null,
  paymentProvider: PaymentProviderType.SHOPIFY,
  isStartDateInFuture: true,
};

export const SubscriptionBillingCardV2 = memo(
  function SubscriptionBillingCardV2({
    input,
  }: {
    input: SubscriptionBillingForm;
  }) {
    const team = useRequiredContext(TeamContext);
    const teamTimezone = team._shopify?.iana_timezone || "America/Los_Angeles";
    const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);
    const {
      enabled,
      startDate,
      isStartDateInFuture,
      renewOnPeriodEnd,
      periodFeeInDollars,
      period,
      paymentProvider,
      targetBillingDay,
    } = input.inputs;

    const subscriptionAlreadyStarted = useMemo(() => {
      const alreadyEnabled = !enabled.changed && enabled.value;

      if (!alreadyEnabled) {
        return false;
      }

      const startDateIsTodayOrBefore =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
          now.getTime();

      if (startDateIsTodayOrBefore) {
        return true;
      }

      return false;
    }, [teamTimezone, startDate, enabled, now]);

    const shouldDisableToggle = useMemo(() => {
      const startDateIsInPast =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
          now.getTime();

      return enabled.value && startDateIsInPast;
    }, [enabled, startDate, now, teamTimezone]);

    useEffect(() => {
      const startDateIsNew = startDate.changed;
      if (!startDateIsNew || !startDate.value) {
        if (!isStartDateInFuture.value) {
          isStartDateInFuture.setValue(true);
        }
        return;
      }

      const startDateIsInThePast =
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        getDateStartOfDayInTimezone(now, teamTimezone).getTime();

      if (isStartDateInFuture.value !== !startDateIsInThePast) {
        isStartDateInFuture.setValue(!startDateIsInThePast);
      }
    }, [startDate, isStartDateInFuture, now, teamTimezone]);

    return (
      <>
        <RedoToggle
          description="Enable subscription billing. Once subscription has started, disable by turning off auto renewal."
          disabled={shouldDisableToggle}
          label="Enabled"
          setValue={(val) => {
            enabled.setValue(val);
            renewOnPeriodEnd.setValue(val);
            if (!val) {
              startDate.setValue(null);
            }
          }}
          size="sm"
          value={enabled.value}
        />
        <RedoToggle
          description="Automatically renew subscription at the end of each billing period"
          disabled={!enabled.value}
          label="Auto renewal"
          setValue={renewOnPeriodEnd.setValue}
          size="sm"
          value={renewOnPeriodEnd.value}
        />
        <RedoIncrementDecrement
          description="How much to charge each period, in dollars"
          disabled={!enabled.value}
          fullWidth
          label="Billing period fee"
          min={0}
          prefix="$"
          setValue={periodFeeInDollars.setValue}
          step={10}
          value={periodFeeInDollars.value}
        />
        <LabeledInput
          description="The date when subscription billing will begin"
          label="Start date"
        >
          <DateInput
            disabled={!enabled.value || subscriptionAlreadyStarted}
            value={
              startDate.value
                ? (() => {
                    const storedDate = startDate.value;
                    const zonedDateTime =
                      Temporal.Instant.fromEpochMilliseconds(
                        storedDate.getTime(),
                      ).toZonedDateTimeISO(teamTimezone);
                    return new Date(
                      zonedDateTime.year,
                      zonedDateTime.month - 1,
                      zonedDateTime.day,
                    );
                  })()
                : null
            }
            valueChange={(date) => {
              if (!date) {
                startDate.setValue(date);
              } else {
                startDate.setValue(
                  getDateStartOfDayInTimezone(date, teamTimezone),
                );
              }
            }}
          />
        </LabeledInput>
        <RedoRadioGroup
          description="How often to charge for the subscription"
          disabled={!enabled.value}
          label="Billing period"
          options={Object.values(BillingPeriod).map((periodValue) => ({
            id: periodValue,
            label: billingPeriodToFriendlyUpper[periodValue],
          }))}
          setValue={period.setValue}
          value={period.value}
        />
        <RedoRadioGroup
          description="Select which payment provider to use for billing"
          disabled={!enabled.value}
          label="Payment provider"
          options={Object.values(PaymentProviderType).map((provider) => ({
            id: provider,
            label:
              provider === PaymentProviderType.SHOPIFY
                ? "Shopify"
                : provider === PaymentProviderType.STRIPE
                  ? "Stripe"
                  : provider,
          }))}
          setValue={paymentProvider.setValue}
          value={paymentProvider.value}
        />
        {paymentProvider.value === PaymentProviderType.STRIPE &&
          !teamGetPaymentMethod(team.stripe ?? undefined) && (
            <Text fontSize="xs" textColor="warning">
              Team has no stripe payment method configured. Billing will fail
              until the team adds a payment method.
            </Text>
          )}
        <LabeledInput
          description="Change the billing day of the month (leave empty to keep current day)"
          label="Target billing day"
        >
          <RedoSingleSelectDropdownInput<number>
            disabled={!enabled.value}
            options={TARGET_BILLING_DAY_OPTIONS}
            optionSelected={({ item }) => {
              targetBillingDay.setValue(item.value);
            }}
            placeholder="Select day (1-28)"
            selectedItem={TARGET_BILLING_DAY_OPTIONS.find(
              (o) => o.value === targetBillingDay.value,
            )}
          />
        </LabeledInput>
      </>
    );
  },
);

export interface SubscriptionBillingDefaultOverrides {
  paymentProvider?: PaymentProviderType;
  periodFeeInDollars?: number;
}

export function subscriptionBillingSettingsToFormValue(
  existingSubscriptionBilling: BaseSubscriptionBillingSettings | undefined,
  defaultOverrides: SubscriptionBillingDefaultOverrides = {},
): SubscriptionBillingValue {
  const defaults: SubscriptionBillingValue = {
    ...subscriptionBillingDefault,
    ...defaultOverrides,
  };

  return existingSubscriptionBilling
    ? {
        enabled: existingSubscriptionBilling.enabled,
        renewOnPeriodEnd: existingSubscriptionBilling.renewOnPeriodEnd,
        startDate: existingSubscriptionBilling.startDate
          ? new Date(existingSubscriptionBilling.startDate)
          : null,
        period: existingSubscriptionBilling.period,
        periodFeeInDollars: centsToDollars(
          existingSubscriptionBilling.periodFeeInCents as UsdCents,
        ),
        targetBillingDay: existingSubscriptionBilling.targetBillingDay ?? null,
        paymentProvider:
          existingSubscriptionBilling.paymentProvider ??
          defaults.paymentProvider,
        isStartDateInFuture: true,
      }
    : defaults;
}

export function subscriptionBillingFormValueToTeam(
  value: SubscriptionBillingValue,
) {
  return {
    enabled: value.enabled,
    renewOnPeriodEnd: value.renewOnPeriodEnd,
    startDate: value.startDate,
    period: value.period,
    periodFeeInCents: dollarsToCents(value.periodFeeInDollars as UsdDollars),
    targetBillingDay: value.targetBillingDay ?? undefined,
    paymentProvider: value.paymentProvider,
  };
}
